import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ScrollText, RefreshCw, ChevronLeft, ChevronRight, Search, Filter, Download,
} from 'lucide-react';
import { exportCsv } from '../lib/exportCsv';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { formatDateTime } from '../lib/formatters';
import { TableSkeleton } from '../components/TableSkeleton';

interface Transaction {
  id: string;
  type: string;
  worldId: string | null;
  playerId: string | null;
  amount: string;
  currency: string | null;
  debitAccount: string | null;
  creditAccount: string | null;
  description: string | null;
  createdAt: string;
}

interface TxPage {
  transactions: Transaction[];
  total: number;
  page: number;
  limit: number;
}

const TX_TYPES = ['', 'EXCHANGE', 'TIP', 'FUND', 'SEED_WORLD', 'SEED_HOLDER', 'CHECKOUT', 'WORLD_SWAP', 'ADJUST'];

function TypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    EXCHANGE:     'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
    TIP:          'text-purple-400 bg-purple-400/10 border-purple-400/20',
    FUND:         'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    SEED_WORLD:   'text-amber-400 bg-amber-400/10 border-amber-400/20',
    SEED_HOLDER:  'text-amber-400 bg-amber-400/10 border-amber-400/20',
    CHECKOUT:     'text-blue-400 bg-blue-400/10 border-blue-400/20',
    WORLD_SWAP:   'text-pink-400 bg-pink-400/10 border-pink-400/20',
    ADJUST:       'text-red-400 bg-red-400/10 border-red-400/20',
  };
  const cls = colors[type] ?? 'text-slate-400 bg-slate-400/10 border-slate-400/20';
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-md border ${cls}`}>
      {type}
    </span>
  );
}

export function AuditLogPage() {
  const role = useAuthStore((s) => s.role);
  const isPartner = role === 'WORLD_OWNER';

  const [page, setPage] = useState(1);
  const [worldId, setWorldId] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [type, setType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [description, setDescription] = useState('');
  const LIMIT = 50;

  const params = new URLSearchParams();
  if (worldId)     params.set('worldId',     worldId);
  if (playerId)    params.set('playerId',    playerId);
  if (type)        params.set('type',        type);
  if (fromDate)    params.set('fromDate',    fromDate);
  if (toDate)      params.set('toDate',      toDate);
  if (amountMin)   params.set('amountMin',   amountMin);
  if (amountMax)   params.set('amountMax',   amountMax);
  if (description) params.set('description', description);
  params.set('page',  String(page));
  params.set('limit', String(LIMIT));

  const { data, isLoading, isFetching, refetch } = useQuery<TxPage>({
    queryKey: ['audit-log', worldId, playerId, type, fromDate, toDate, amountMin, amountMax, description, page],
    queryFn: () =>
      api.get(`/ledger/transactions?${params.toString()}`).then((r) => r.data),
    placeholderData: (previousData) => previousData,
  });

  const transactions = data?.transactions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  function resetFilters() {
    setWorldId(''); setPlayerId(''); setType('');
    setFromDate(''); setToDate(''); setAmountMin(''); setAmountMax(''); setDescription(''); setPage(1);
  }

  const inputCls =
    'bg-[#0d1526] border border-white/[0.06] rounded-lg px-3 py-2 text-xs text-slate-300 placeholder-slate-700 focus:outline-none focus:border-cyan-500/40 w-full';

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
            <ScrollText className="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Audit Log</h2>
            <p className="text-sm text-slate-600 mt-0.5">
              {total > 0 ? `${total.toLocaleString()} transactions` : 'Transaction ledger'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCsv(transactions, `audit-log-${new Date().toISOString().slice(0,10)}.csv`, [
              { key: 'id', label: 'TX ID' },
              { key: 'type', label: 'Type' },
              { key: 'worldId', label: 'World ID' },
              { key: 'playerId', label: 'Player ID' },
              { key: 'amount', label: 'Amount' },
              { key: 'currency', label: 'Currency' },
              { key: 'debitAccount', label: 'Debit Account' },
              { key: 'creditAccount', label: 'Credit Account' },
              { key: 'description', label: 'Description' },
              { key: 'createdAt', label: 'Created At' },
            ])}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white border border-white/[0.06] hover:border-white/10 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white border border-white/[0.06] hover:border-white/10 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-xs font-semibold text-slate-400 tracking-wide">FILTERS</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
          {!isPartner && (
            <input
              className={inputCls}
              placeholder="World ID"
              value={worldId}
              onChange={(e) => { setWorldId(e.target.value); setPage(1); }}
            />
          )}
          <input
            className={inputCls}
            placeholder="Player / Holder ID"
            value={playerId}
            onChange={(e) => { setPlayerId(e.target.value); setPage(1); }}
          />
          <select
            className={inputCls}
            value={type}
            onChange={(e) => { setType(e.target.value); setPage(1); }}
          >
            <option value="">All types</option>
            {TX_TYPES.filter(Boolean).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input
            type="date"
            className={inputCls}
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
          />
          <input
            type="date"
            className={inputCls}
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
          />
          <input
            type="number"
            className={inputCls}
            placeholder="Min amount"
            value={amountMin}
            onChange={(e) => { setAmountMin(e.target.value); setPage(1); }}
          />
          <input
            type="number"
            className={inputCls}
            placeholder="Max amount"
            value={amountMax}
            onChange={(e) => { setAmountMax(e.target.value); setPage(1); }}
          />
          <input
            className={inputCls}
            placeholder="Description search"
            value={description}
            onChange={(e) => { setDescription(e.target.value); setPage(1); }}
          />
          <button
            onClick={resetFilters}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs text-slate-500 hover:text-slate-300 border border-white/[0.06] hover:border-white/10 transition-colors"
          >
            <Search className="w-3 h-3" /> Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <motion.div
        className="card overflow-hidden"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/[0.04]">
                {['Timestamp', 'Type', 'World', 'Player / Holder', 'Amount', 'Currency', 'Debit → Credit', 'Description'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-slate-600 tracking-widest uppercase whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <TableSkeleton rows={8} cols={8} />
              )}
              {!isLoading && transactions.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-700 text-xs">
                    No transactions match the current filters.
                  </td>
                </tr>
              )}
              {transactions.map((tx) => (
                <tr
                  key={tx.id}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap">
                    {formatDateTime(tx.createdAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <TypeBadge type={tx.type} />
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap">
                    {tx.worldId ? (
                      <span className="text-slate-400">{tx.worldId.slice(0, 8)}…</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap">
                    {tx.playerId ? (
                      <span className="text-slate-400">{tx.playerId.slice(0, 8)}…</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-slate-300 whitespace-nowrap text-right">
                    {Number(tx.amount).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {tx.currency ?? 'UKC'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-[10px] whitespace-nowrap">
                    {tx.debitAccount && tx.creditAccount
                      ? `${tx.debitAccount} → ${tx.creditAccount}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate">
                    {tx.description ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {page} of {totalPages} &bull; {total.toLocaleString()} total
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
